import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { EMPTY, Subject, catchError, filter, switchMap, take, takeUntil } from 'rxjs';
import { Chart, registerables } from 'chart.js';
import { AuthService } from '../../../../services/auth.service';
import { MarketingAcquisitionService, RetentionMetricsDto, RetentionReportDto } from '../../../../services/marketing-acquisition.service';
import { ChartWrapperComponent } from '../../../charts/shared/chart-wrapper/chart-wrapper.component';
import { EntityChartDataDto } from '../../../../models/chart.models';
import type { AcquisitionDateRange } from '../acquisition.component';

// Don't rely on another component having registered Chart.js first (re-registering is a no-op).
Chart.register(...registerables);

/**
 * V4 — first vs repeat pickups by acquisition channel, for customers whose account was created in
 * the range. Reads permanent customer and collection data, so it works for any past range.
 */
@Component({
  selector: 'app-acquisition-retention',
  standalone: true,
  imports: [CommonModule, ChartWrapperComponent],
  templateUrl: './retention.component.html'
})
export class RetentionComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) range!: AcquisitionDateRange;

  loading = false;
  errorMessage = '';
  report: RetentionReportDto | null = null;
  repeatRateChart: EntityChartDataDto | null = null;

  private readonly load$ = new Subject<AcquisitionDateRange>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private acquisitionService: MarketingAcquisitionService,
    private authService: AuthService
  ) { }

  ngOnInit(): void {
    this.load$
      .pipe(
        switchMap(range => {
          this.loading = true;
          this.errorMessage = '';
          return this.acquisitionService.getRetention(range.from, range.to).pipe(
            catchError(error => {
              this.onError(error);
              return EMPTY;
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(report => this.onReport(report));

    this.authService.getMsalInitializedObservable()
      .pipe(filter(initialized => initialized), take(1), takeUntil(this.destroy$))
      .subscribe(() => this.load$.next(this.range));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['range'] && !changes['range'].firstChange) {
      this.load$.next(this.range);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Share of all pickup revenue that came from repeat pickups, in percent. */
  repeatRevenueShare(metrics: RetentionMetricsDto): number | null {
    const total = metrics.firstPickupRevenue + metrics.repeatRevenue;
    return total === 0 ? null : Math.round((1000 * metrics.repeatRevenue) / total) / 10;
  }

  private onReport(report: RetentionReportDto): void {
    this.report = report;
    this.repeatRateChart = {
      entityName: 'Repeat rate (%)',
      totalCount: report.totals.repeatRate ?? 0,
      dataPoints: report.rows.map(r => ({ label: r.label, value: r.repeatRate ?? 0 }))
    };
    this.loading = false;
  }

  private onError(error: { status?: number; error?: { error?: string } }): void {
    this.loading = false;
    this.report = null;
    if (error.status === 400 && error.error?.error) {
      this.errorMessage = error.error.error;
    } else if (error.status === 403) {
      this.errorMessage = 'Access denied. You do not have permission to view marketing reports.';
    } else {
      this.errorMessage = 'Failed to load retention. Please try again.';
    }
  }
}
